import React, { useState, useEffect, useRef, Fragment } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  Stack,
  Card,
  CardContent,
  Divider,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Campaign as CampaignIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';

interface Lead {
  _id: string;
  leadId: string;
  originalData: any;
  emailValidationStatus?: 'valid' | 'invalid' | 'pending';
  emailValidationDate?: string;
  communication?: {
    emailSent: boolean;
    emailSentAt?: string;
    emailOpened: boolean;
    emailOpenedAt?: string;
    emailClicked: boolean;
    emailClickedAt?: string;
    emailDeleted: boolean;
    emailDeletedAt?: string;
    emailSpam: boolean;
    emailSpamAt?: string;
    emailAbandoned: boolean;
    emailAbandonedAt?: string;
    emailBounced: boolean;
    emailBouncedAt?: string;
    emailDelivered?: boolean;
    emailDeliveredAt?: string;
    emailDropped?: boolean;
    emailDroppedAt?: string;
    emailDropReason?: string;
    interactionStatus?: 'delivered' | 'opened' | 'clicked' | 'deleted' | 'spam' | 'abandoned' | 'bounced' | 'dropped' | 'unsubscribed' | 'not_sent';
    whatsappSent: boolean;
    whatsappSentAt?: string;
    whatsappDelivered: boolean;
    whatsappRead: boolean;
  } | null;
}

interface Template {
  _id: string;
  name: string;
  templateType: string;
  subject: string;
  body: string;
}

const CRM: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [leads, setLeads] = useState<Lead[]>([]);
  
  // Determine base path (mailforge for editors, crm for others)
  const isEditor = user?.role === 'editor';
  const basePath = isEditor ? 'mailforge' : 'crm';
  
  // Get the full base path from current location (e.g., /demo/wissam/mailforge)
  const getFullBasePath = () => {
    const pathParts = location.pathname.split('/');
    // Find the index of 'mailforge' or 'crm' in the path
    const crmIndex = pathParts.findIndex((part: string) => part === 'mailforge' || part === 'crm');
    if (crmIndex !== -1) {
      // Return path up to and including mailforge/crm
      return pathParts.slice(0, crmIndex + 1).join('/');
    }
    // Fallback: construct from company slug and username
    if (pathParts.length >= 3) {
      return `/${pathParts[1]}/${pathParts[2]}/${basePath}`;
    }
    return `/${basePath}`;
  };
  
  // Redirect editors from /crm to /mailforge
  useEffect(() => {
    if (isEditor && location.pathname.includes('/crm')) {
      const newPath = location.pathname.replace('/crm', '/mailforge');
      if (newPath !== location.pathname) {
        navigate(newPath, { replace: true });
      }
    }
  }, [isEditor, location.pathname, navigate]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [campaignLeads, setCampaignLeads] = useState<string[]>([]);
  
  // FROM email address - always the editor's email
  const [fromEmailAddress, setFromEmailAddress] = useState<string>('');
  
  // Dialog states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [manualEmailDialogOpen, setManualEmailDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  
  // Form states
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [manualSubject, setManualSubject] = useState('');
  const [manualBody, setManualBody] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [campaignTemplate, setCampaignTemplate] = useState('');
  
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Fetch leads
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/crm/leads');
      setLeads(response.data.leads || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/crm/templates');
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  // Set FROM email address to editor's registered email
  useEffect(() => {
    if (user?.email) {
      setFromEmailAddress(user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchLeads();
    fetchTemplates();
    
    // Initialize email from user's registered email
    if (user?.email) {
      setFromEmailAddress(user.email);
    }
  }, [user?.email]);

  // Refresh leads when window/tab gains focus (user enters/returns to CRM tab)
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 [CRM] Window focused - refreshing leads...');
      fetchLeads();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []); // Only run once on mount


  // Handle send email with template
  const handleSendEmail = async () => {
    if (!selectedLead || !selectedTemplate) return;

    try {
      setSending(true);
      setError(null);
      
      if (!user?.email) {
        setError('Your email address is required. Please contact support.');
        return;
      }

      const response = await api.post('/api/crm/send-email', {
        leadId: selectedLead.leadId,
        templateId: selectedTemplate,
        senderEmail: user.email
      });

      if (response.data.success) {
        setSuccess('Email sent successfully!');
        setEmailDialogOpen(false);
        fetchLeads();
      } else {
        setError(response.data.error || 'Failed to send email');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Handle send manual email
  const handleSendManualEmail = async () => {
    if (!selectedLead || !manualSubject || !manualBody) return;

    try {
      setSending(true);
      setError(null);
      
      if (!user?.email) {
        setError('Your email address is required. Please contact support.');
        return;
      }

      const response = await api.post('/api/crm/send-email-manual', {
        leadId: selectedLead.leadId,
        subject: manualSubject,
        body: manualBody,
        senderEmail: user.email
      });

      if (response.data.success) {
        setSuccess('Email sent successfully!');
        setManualEmailDialogOpen(false);
        setManualSubject('');
        setManualBody('');
        fetchLeads();
      } else {
        setError(response.data.error || 'Failed to send email');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Handle WhatsApp link generation
  const handleWhatsApp = async () => {
    if (!selectedLead) return;

    try {
      setSending(true);
      setError(null);
      
      const message = whatsappMessage || 'Hello';
      const response = await api.get(
        `/api/crm/whatsapp-link/${selectedLead.leadId}?message=${encodeURIComponent(message)}`
      );

      if (response.data.success) {
        // Open WhatsApp link in new tab
        window.open(response.data.link, '_blank');
        setWhatsappDialogOpen(false);
        setWhatsappMessage('');
        fetchLeads();
      } else {
        setError(response.data.error || 'Failed to generate WhatsApp link');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate WhatsApp link');
    } finally {
      setSending(false);
    }
  };

  // Get lead name with proper fallback logic
  const getLeadName = (lead: Lead) => {
    const data = lead.originalData || {};
    
    // Priority 1: Full Name if exists
    if (data['Full Name']) return data['Full Name'];
    
    // Priority 2: First Name + Last Name if both exist
    if (data['First Name'] && data['Last Name']) {
      return `${data['First Name']} ${data['Last Name']}`;
    }
    
    // Priority 3: First Name only if no Last Name
    if (data['First Name'] && !data['Last Name']) {
      return data['First Name'];
    }
    
    // Priority 4: Family Name if no First Name
    if (!data['First Name'] && data['Family Name']) {
      return data['Family Name'];
    }
    
    // Priority 5: Last Name as fallback
    if (data['Last Name']) return data['Last Name'];
    
    return 'Unknown';
  };

  // Get lead email
  const getLeadEmail = (lead: Lead) => {
    const data = lead.originalData || {};
    return data['Work Email'] || data['Email'] || 'N/A';
  };

  // Get lead company
  const getLeadCompany = (lead: Lead) => {
    const data = lead.originalData || {};
    return data['Company Name'] || data['Company'] || 'N/A';
  };

  // Toggle lead selection
  const toggleLeadSelection = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  // Add selected leads to campaign
  const addToCampaign = () => {
    setCampaignLeads([...campaignLeads, ...Array.from(selectedLeads)]);
    setSelectedLeads(new Set());
  };

  // Handle row expansion
  const handleExpandRow = (leadId: string) => {
    setExpandedRow(expandedRow === leadId ? null : leadId);
  };

  // Check if user can send (Editor only)
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          CRM
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={() => navigate(`${getFullBasePath()}/mailbox`)}
          >
            Mailbox
          </Button>
          {isEditor && (
            <>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => navigate(`${getFullBasePath()}/automations`)}
              >
                Automations
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => navigate(`${getFullBasePath()}/templates`)}
              >
                Manage Email Templates
              </Button>
            </>
          )}
        </Stack>
      </Box>
      
      {(isAdmin || isViewer) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isAdmin 
            ? 'View-only mode: You can see email status for all assigned leads. Only Editors can send emails.'
            : 'View-only mode: You can see email status for your assigned leads.'}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Campaign Box - Only for Editors */}
      {isEditor && campaignLeads.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Campaign Box ({campaignLeads.length} leads)
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<CampaignIcon />}
                onClick={() => setCampaignDialogOpen(true)}
              >
                Send Campaign
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => setCampaignLeads([])}
              >
                Clear
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Actions Bar - Only for Editors */}
      {isEditor && selectedLeads.size > 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addToCampaign}
          >
            Add {selectedLeads.size} to Campaign
          </Button>
        </Box>
      )}

      {/* Leads Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {isEditor && <TableCell padding="checkbox" />}
              <TableCell>Full Name</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Email Verification</TableCell>
              {isEditor && <TableCell>Actions</TableCell>}
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.map((lead) => (
              <React.Fragment key={lead._id}>
              <TableRow>
                {isEditor && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedLeads.has(lead.leadId)}
                      onChange={() => toggleLeadSelection(lead.leadId)}
                    />
                  </TableCell>
                )}
                <TableCell>{getLeadName(lead)}</TableCell>
                <TableCell>{getLeadCompany(lead)}</TableCell>
                <TableCell>{getLeadEmail(lead)}</TableCell>
                <TableCell>
                  {lead.emailValidationStatus === 'valid' ? (
                    <Chip label="Verified" color="success" size="small" />
                  ) : lead.emailValidationStatus === 'invalid' ? (
                    <Chip label="Invalid" color="error" size="small" />
                  ) : (
                    <Chip label="Pending" color="warning" size="small" />
                  )}
                </TableCell>
                {isEditor && (
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Send Email with Template">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLead(lead);
                            setEmailDialogOpen(true);
                          }}
                        >
                          <EmailIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Manual Email">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLead(lead);
                            setManualEmailDialogOpen(true);
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send WhatsApp">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLead(lead);
                            setWhatsappDialogOpen(true);
                          }}
                        >
                          <WhatsAppIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                )}
                <TableCell align="center">
                  <Tooltip title={expandedRow === lead.leadId ? 'Collapse row' : 'Expand row'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpandRow(lead.leadId);
                      }}
                      sx={{
                        backgroundColor: expandedRow === lead.leadId ? '#FEE2E2' : '#CCF4CD',
                        color: expandedRow === lead.leadId ? '#DC2626' : '#00C805',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          backgroundColor: expandedRow === lead.leadId ? '#FECACA' : '#B8E6B8',
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      {expandedRow === lead.leadId ? (
                        <RemoveIcon sx={{ fontSize: 16, transition: 'transform 0.3s ease-in-out' }} />
                      ) : (
                        <AddIcon sx={{ fontSize: 16, transition: 'transform 0.3s ease-in-out' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              
              {/* Expanded Row */}
              <TableRow>
                <TableCell colSpan={isEditor ? 6 : 5} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                  <Collapse in={expandedRow === lead.leadId} timeout="auto" unmountOnExit>
                    <Box sx={{ 
                      padding: 3, 
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', 
                      borderRadius: 2,
                      margin: 1,
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}
                      >
                        Communication Status
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Delivered</Typography>
                          <Box sx={{ mb: 1 }}>
                            {lead.communication?.emailDelivered ? (
                              <Chip label="✓" color="success" size="small" sx={{ minWidth: '40px' }} />
                            ) : (
                              <Chip label="—" size="small" sx={{ minWidth: '40px' }} variant="outlined" />
                            )}
                          </Box>
                          {lead.communication?.emailDeliveredAt && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(lead.communication.emailDeliveredAt).toLocaleString()}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Opened</Typography>
                          <Box sx={{ mb: 1 }}>
                            {lead.communication?.emailOpened ? (
                              <Chip label="✓" color="success" size="small" sx={{ minWidth: '40px' }} />
                            ) : (
                              <Chip label="—" size="small" sx={{ minWidth: '40px' }} variant="outlined" />
                            )}
                          </Box>
                          {lead.communication?.emailOpenedAt && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(lead.communication.emailOpenedAt).toLocaleString()}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Clicked</Typography>
                          <Box sx={{ mb: 1 }}>
                            {lead.communication?.emailClicked ? (
                              <Chip label="✓" color="warning" size="small" sx={{ minWidth: '40px' }} />
                            ) : (
                              <Chip label="—" size="small" sx={{ minWidth: '40px' }} variant="outlined" />
                            )}
                          </Box>
                          {lead.communication?.emailClickedAt && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(lead.communication.emailClickedAt).toLocaleString()}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Spam</Typography>
                          <Box sx={{ mb: 1 }}>
                            {lead.communication?.emailSpam ? (
                              <Chip label="✗" color="error" size="small" sx={{ minWidth: '40px' }} />
                            ) : (
                              <Chip label="—" size="small" sx={{ minWidth: '40px' }} variant="outlined" />
                            )}
                          </Box>
                          {lead.communication?.emailSpamAt && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(lead.communication.emailSpamAt).toLocaleString()}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Bounced</Typography>
                          <Box sx={{ mb: 1 }}>
                            {lead.communication?.emailBounced ? (
                              <Chip label="✗" color="error" size="small" sx={{ minWidth: '40px' }} />
                            ) : (
                              <Chip label="—" size="small" sx={{ minWidth: '40px' }} variant="outlined" />
                            )}
                          </Box>
                          {lead.communication?.emailBouncedAt && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(lead.communication.emailBouncedAt).toLocaleString()}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>WhatsApp</Typography>
                          <Box sx={{ mb: 1 }}>
                            {lead.communication?.whatsappSent ? (
                              <Stack direction="row" spacing={1}>
                                <Chip label="Sent" color="success" size="small" />
                                {lead.communication.whatsappRead && (
                                  <Chip label="Read" color="info" size="small" />
                                )}
                              </Stack>
                            ) : (
                              <Chip label="Not Sent" size="small" />
                            )}
                          </Box>
                          {lead.communication?.whatsappSentAt && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(lead.communication.whatsappSentAt).toLocaleString()}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Send Email Dialog (Template) */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Email with Template</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              label="Select Template"
            >
              {templates.map((template) => (
                <MenuItem key={template._id} value={template._id}>
                  {template.name} ({template.templateType})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* FROM Address (Read-only, dynamically generated) */}
          <TextField
            fullWidth
            label="From Email Address"
            value={fromEmailAddress}
            disabled
            sx={{ mt: 2 }}
            helperText="All emails are sent from this address. Leads will see this as the sender."
          />
          
          {/* Reply-To Address (Editor's registered email - read-only) */}
          <TextField
            fullWidth
            label="Reply-To Email Address"
            value={user?.email || ''}
            disabled
            sx={{ mt: 2 }}
            helperText="Replies from leads will be sent to your registered email address."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={!selectedTemplate || !user?.email || sending}
          >
            {sending ? <CircularProgress size={20} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Manual Email Dialog */}
      <Dialog open={manualEmailDialogOpen} onClose={() => setManualEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Manual Email</DialogTitle>
        <DialogContent>
          {/* FROM Address (Read-only, dynamically generated) */}
          <TextField
            fullWidth
            label="From Email Address"
            value={fromEmailAddress}
            disabled
            sx={{ mt: 2 }}
            helperText="All emails are sent from this address. Leads will see this as the sender."
          />
          
          {/* Reply-To Address (Editor's registered email - read-only) */}
          <TextField
            fullWidth
            label="Reply-To Email Address"
            value={user?.email || ''}
            disabled
            sx={{ mt: 2 }}
            helperText="Replies from leads will be sent to your registered email address."
          />
          
          <TextField
            fullWidth
            label="Subject"
            value={manualSubject}
            onChange={(e) => setManualSubject(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={10}
            value={manualBody}
            onChange={(e) => setManualBody(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualEmailDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendManualEmail}
            variant="contained"
            disabled={!user?.email || !manualSubject || !manualBody || sending}
          >
            {sending ? <CircularProgress size={20} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog open={whatsappDialogOpen} onClose={() => setWhatsappDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send WhatsApp Message</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Message (optional)"
            multiline
            rows={4}
            value={whatsappMessage}
            onChange={(e) => setWhatsappMessage(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Leave empty for default message"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhatsappDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleWhatsApp}
            variant="contained"
            disabled={sending}
            startIcon={<WhatsAppIcon />}
          >
            {sending ? <CircularProgress size={20} /> : 'Open WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Dialog */}
      <Dialog open={campaignDialogOpen} onClose={() => setCampaignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Campaign</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Campaign Name"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={campaignTemplate}
              onChange={(e) => setCampaignTemplate(e.target.value)}
              label="Select Template"
            >
              {templates.map((template) => (
                <MenuItem key={template._id} value={template._id}>
                  {template.name} ({template.templateType})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* FROM Address (Read-only, dynamically generated) */}
          <TextField
            fullWidth
            label="From Email Address"
            value={fromEmailAddress}
            disabled
            sx={{ mt: 2 }}
            helperText="All emails are sent from this address. Leads will see this as the sender."
          />
          
          {/* Reply-To Address (Editor's registered email - read-only) */}
          <TextField
            fullWidth
            label="Reply-To Email Address"
            value={user?.email || ''}
            disabled
            sx={{ mt: 2 }}
            helperText="Replies from leads will be sent to your registered email address."
          />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            This will queue and send emails to {campaignLeads.length} leads with a 5-6 second delay between each send to maintain email reputation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!campaignName || !campaignTemplate || !user?.email || sending}
            onClick={async () => {
              if (!campaignName || !campaignTemplate || !user?.email) return;
              
              try {
                setSending(true);
                setError(null);
                
                // Create campaign
                const campaignResponse = await api.post('/api/crm/campaigns', {
                  name: campaignName,
                  leadIds: campaignLeads,
                  templateId: campaignTemplate,
                  messageType: 'email'
                });
                
                if (campaignResponse.data.success) {
                  // Start sending campaign
                  const sendResponse = await api.post(
                    `/api/crm/campaigns/${campaignResponse.data.campaign._id}/send`,
                    { senderEmail: user?.email }
                  );
                  
                  if (sendResponse.data.success) {
                    setSuccess(`Campaign "${campaignName}" started! Emails are being queued and sent with 5-6 second delays.`);
                    setCampaignDialogOpen(false);
                    setCampaignName('');
                    setCampaignTemplate('');
                    setCampaignLeads([]);
                    fetchLeads();
                  } else {
                    setError(sendResponse.data.error || 'Failed to start campaign');
                  }
                } else {
                  setError(campaignResponse.data.error || 'Failed to create campaign');
                }
              } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to send campaign');
              } finally {
                setSending(false);
              }
            }}
          >
            {sending ? <CircularProgress size={20} /> : 'Send Campaign'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CRM;

